/**
 * Formen der API-Antworten.
 *
 * Von Hand geführt und nicht aus dem OpenAPI-Schema erzeugt: die Datei ist
 * überschaubar, und die deutschen Feldnamen sollen auch hier lesbar bleiben.
 * Weicht sie vom Server ab, fällt das beim ersten Aufruf auf.
 */

export type Stufe = 'o.B.' | '1' | '2' | '3' | '--'
export type Rolle = 'admin' | 'inhaber' | 'pruefer' | 'buero' | 'betrachter'
export type Pruefart = 'abnahme' | 'hauptuntersuchung' | 'operativ' | 'nachpruefung'
export type Pruefstatus = 'entwurf' | 'laufend' | 'abgeschlossen'
export type Mangelstatus = 'offen' | 'gemeldet' | 'behoben' | 'hinfaellig'

export const MANGELSTATUSTEXT: Record<Mangelstatus, string> = {
  offen: 'offen',
  gemeldet: 'als behoben gemeldet',
  behoben: 'behoben und bestätigt',
  hinfaellig: 'hinfällig',
}
export type Bausteinkategorie = 'mangel' | 'bemerkung' | 'normverweis'
export type Fotoart = 'uebersicht' | 'detail'

/** Alle Stufen in der Reihenfolge, in der sie im Prüfbogen stehen. */
export const STUFEN: Stufe[] = ['o.B.', '1', '2', '3', '--']

export const STUFENTEXT: Record<Stufe, string> = {
  'o.B.': 'ohne Beanstandung',
  '1': 'geringer Mangel',
  '2': 'erheblicher Mangel',
  '3': 'gefährlich – Gerät sperren',
  '--': 'nicht vorhanden',
}

/** CSS-Klasse je Stufe. `null` heißt unbeurteilt. */
export function stufenklasse(stufe: Stufe | null): string {
  if (stufe === null) return 'stufe stufe-offen'
  if (stufe === 'o.B.') return 'stufe stufe-ob'
  if (stufe === '--') return 'stufe stufe-na'
  return `stufe stufe-${stufe}`
}

export function istMangel(stufe: Stufe | null): boolean {
  return stufe === '1' || stufe === '2' || stufe === '3'
}

export const PRUEFARTTEXT: Record<Pruefart, string> = {
  abnahme: 'Abnahmeprüfung',
  hauptuntersuchung: 'Jährliche Hauptuntersuchung',
  operativ: 'Operative Inspektion',
  nachpruefung: 'Nachprüfung',
}

/* Die Kundenart ist jetzt ein Katalog und keine feste Liste mehr.

   Am Kunden steht der Name als Text – dieselbe Entscheidung wie beim
   Gerätetyp: ein umbenannter oder entfernter Katalogeintrag darf einen
   vorhandenen Kunden nicht unlesbar machen. Deshalb gibt es hier keine
   Übersetzungstabelle mehr; angezeigt wird, was dasteht. */
export type Kundenartkatalog = {
  id: string
  name: string
  sortierung: number
  aktiv: boolean
}

export const ROLLENTEXT: Record<Rolle, string> = {
  admin: 'Administration',
  inhaber: 'Inhaber',
  pruefer: 'Prüfer',
  buero: 'Büro',
  betrachter: 'Betrachter',
}

// ── Konto ────────────────────────────────────────────────────────

export type EigenesKonto = {
  id: string
  email: string
  name: string
  rolle: Rolle
  letzte_anmeldung: string | null
  mandant_id: string
  mandant_name: string
  kunden_eingeschraenkt: boolean
  hat_passwort: boolean
  telefon: string | null
  zertifikat: string | null
  /** Die hinterlegte Unterschrift als PNG-Datenurl. */
  unterschrift: string | null
  /** Ob die Prüftermine in den Outlook-Kalender geschrieben werden. */
  kalender_sync: boolean
  /** Die tatsächlichen Rechte, aus Rolle und Abweichungen gerechnet. */
  rechte: string[]
  /** Steht über den Mandanten: darf Prüfbüros anlegen und sperren. */
  systemverwalter: boolean
  /** Wahr, solange eine fremde Ansicht übernommen ist. */
  handelt_als: boolean
  handelnder_name: string | null
  handelnder_email: string | null
}

export type Anmeldeantwort = {
  benutzer: { id: string; email: string; name: string; rolle: Rolle }
  csrf_token: string
}

// ── Stammdaten ───────────────────────────────────────────────────

/** Befugnis zur Eigenkontrolle nach DIN EN 1176-7. */
export type Eigenkontrolle = 'keine' | 'sicht' | 'sicht_und_operativ'

export const EIGENKONTROLLETEXT: Record<Eigenkontrolle, string> = {
  keine: 'Keine — nur wir prüfen',
  sicht: 'Visuelle Routinekontrolle',
  sicht_und_operativ: 'Routinekontrolle und operative Inspektion',
}

export type Kunde = {
  id: string
  name: string
  /** Die Art, unter der der Kunde geführt wird. Aus dem Katalog gewählt. */
  art: string | null
  kundennummer: string | null
  strasse: string | null
  plz: string | null
  ort: string | null
  ansprechpartner: string | null
  email: string | null
  telefon: string | null
  bemerkung: string | null
  aktiv: boolean
  /** Was das Personal dieses Kunden selbst kontrollieren darf. */
  eigenkontrolle: Eigenkontrolle
  /** Darf der Betreiber Fotos zu seiner Kontrolle hochladen? */
  eigenkontrolle_fotos: boolean
  geaendert_am: string
  anzahl_spielplaetze: number
  offene_maengel: number
}

export type Spielplatz = {
  id: string
  kunde_id: string
  name: string
  /** Die System-ID, sechsstellig, je Prüfbüro eindeutig über Plätze und
   *  Geräte hinweg. Steht neben `platznummer` – das ist die Nummer des
   *  Prüfbüros aus dem alten Protokoll und wird nie überschrieben. */
  kennung: string | null
  platznummer: string | null
  strasse: string | null
  plz: string | null
  ort: string | null
  breitengrad: number | null
  laengengrad: number | null
  baujahr: number | null
  flaeche_qm: number | null
  altersgruppe: string | null
  routenziel: string | null
  bemerkung: string | null
  aktiv: boolean
  /** Abstand der Eigenkontrolle durch den Betreiber, in Tagen. */
  eigenkontrolle_tage: number | null
  geaendert_am: string
  kunde_name: string
  anzahl_geraete: number
  letzte_pruefung_am: string | null
  naechste_pruefung_am: string | null
  offene_maengel: number
}

export type Geraet = {
  id: string
  spielplatz_id: string
  bezeichnung: string
  geraetetyp: string | null
  /** Siehe `Spielplatz.kennung`. */
  kennung: string | null
  geraetenummer: string | null
  hersteller: string | null
  baujahr: number | null
  seriennummer: string | null
  standort_hinweis: string | null
  fallschutzart: string | null
  fallhoehe_cm: number | null
  sortierung: number
  ausgemustert_am: string | null
  bemerkung: string | null
  geaendert_am: string
}

// ── Kataloge ─────────────────────────────────────────────────────

export type Platzkriterium = {
  id: string
  gruppe: string
  bezeichnung: string
  hinweis: string | null
  normverweis: string | null
  sortierung: number
  aktiv: boolean
}

export type Geraetetyp = {
  id: string
  name: string
  normteil: string | null
  sortierung: number
  aktiv: boolean
}

export type Textbaustein = {
  id: string
  kategorie: Bausteinkategorie
  text: string
  stufe_vorschlag: Stufe | null
  normverweis: string | null
  geraetetyp: string | null
  verwendungen: number
  aktiv: boolean
}

// ── Prüfung ──────────────────────────────────────────────────────

export type Pruefung = {
  id: string
  spielplatz_id: string
  spielplatz_name: string
  kunde_id: string
  kunde_name: string
  pruefart: Pruefart
  status: Pruefstatus
  pruefer_id: string | null
  pruefer_name: string | null
  berichtsnummer: string | null
  datum: string
  begonnen_am: string | null
  abgeschlossen_am: string | null
  wetter: string | null
  temperatur_c: number | null
  begleitung: string | null
  zusammenfassung: string | null
  naechste_pruefung_am: string | null
  vorgaenger_id: string | null
  geaendert_am: string

  /* Die angekreuzten Prüfgrundlagen. Sie stehen auf der Bescheinigung. */
  grundlage_en1176: boolean
  grundlage_dguv_202_022: boolean
  grundlage_dguv_r102_002: boolean
  grundlage_din18034: boolean
  grundlage_en16630: boolean
}

/**
 * Die Normen zum Ankreuzen, in der Reihenfolge der Bescheinigung.
 *
 * Der Kurztext daneben sagt, wofür die Norm gilt – „DIN 18034" allein
 * beantwortet die Frage nicht, ob sie hier anzukreuzen ist.
 */
export const PRUEFGRUNDLAGEN: {
  feld: keyof Pick<
    Pruefung,
    | 'grundlage_en1176'
    | 'grundlage_dguv_202_022'
    | 'grundlage_dguv_r102_002'
    | 'grundlage_din18034'
    | 'grundlage_en16630'
  >
  norm: string
  wofuer: string
}[] = [
  {
    feld: 'grundlage_en1176',
    norm: 'DIN EN 1176 / DIN EN 1177',
    wofuer: 'Öffentliche Spielplatzgeräte und Fallschutz',
  },
  {
    feld: 'grundlage_dguv_202_022',
    norm: 'DGUV 202-022',
    wofuer: 'Schule und Kita',
  },
  {
    feld: 'grundlage_dguv_r102_002',
    norm: 'DGUV R102-002',
    wofuer: 'Kita: Bau und Ausstattung',
  },
  {
    feld: 'grundlage_din18034',
    norm: 'DIN 18034',
    wofuer: 'Spielplatz und Freifläche zum Spielen',
  },
  {
    feld: 'grundlage_en16630',
    norm: 'DIN EN 16630',
    wofuer: 'Ortsfeste Fitnessgeräte im Freien',
  },
]

export type Platzbefund = {
  id: string
  kriterium_id: string
  gruppe: string
  bezeichnung: string
  hinweis: string | null
  sortierung: number
  stufe: Stufe | null
  beurteilt_am: string | null
  mangel: string | null
  bemerkung: string | null
  normverweis: string | null
  vorjahr_stufe: Stufe | null
  vorjahr_mangel: string | null
  /** Normverweis der Vorprüfung – wird im Bogen vorgeschlagen. */
  vorjahr_normverweis: string | null
  anzahl_fotos: number
}

export type Geraetebefund = {
  id: string
  spielgeraet_id: string
  bezeichnung: string
  geraetetyp: string | null
  /** Die System-ID, sechsstellig. Steht neben der Nummer des Prüfbüros. */
  kennung: string | null
  geraetenummer: string | null
  hersteller: string | null
  baujahr: number | null
  standort_hinweis: string | null
  fallschutzart: string | null
  fallhoehe_cm: number | null
  sortierung: number
  stufe: Stufe | null
  beurteilt_am: string | null
  mangel: string | null
  bemerkung: string | null
  normverweis: string | null
  gesperrt: boolean
  mangelstatus: Mangelstatus | null
  /** Erwartet der Prüfer eine Rückmeldung des Kunden zur Behebung? */
  meldung_erwartet: boolean
  frist_am: string | null
  vorjahr_stufe: Stufe | null
  vorjahr_mangel: string | null
  /** Normverweis der Vorprüfung – wird im Bogen vorgeschlagen. */
  vorjahr_normverweis: string | null
  anzahl_fotos: number
}

export type Fortschritt = {
  punkte_gesamt: number
  punkte_beurteilt: number
  punkte_offen: number
  maengel_gesamt: number
  stufe3_vorhanden: boolean
  naechster_offener_id: string | null
  naechster_offener_art: 'platz' | 'geraet' | null
  abschluss_moeglich: boolean
}

export type Pruefbogen = Pruefung & {
  platzbefunde: Platzbefund[]
  geraetebefunde: Geraetebefund[]
  fortschritt: Fortschritt
}

export type Foto = {
  id: string
  pruefung_id: string
  geraetebefund_id: string | null
  platzbefund_id: string | null
  art: Fotoart
  beschriftung: string | null
  breite: number | null
  hoehe: number | null
  bytes: number | null
  sortierung: number
  erstellt_am: string
}

/** Antwort des Servers, wenn der Abschluss an offenen Punkten scheitert. */
export type AbschlussKonflikt = {
  meldung: string
  punkte_offen: number
  offene_platzkriterien: string[]
  offene_geraete: string[]
}

export type BefundEingabe = {
  stufe: Stufe
  mangel?: string | null
  bemerkung?: string | null
  normverweis?: string | null
  frist_am?: string | null
  /** Soll der Kunde die Behebung melden? Vorgabe: ja. */
  meldung_erwartet?: boolean
}
